/*
 * Motivio journey analytics — browser client.
 *
 * Thin, fire-and-forget wrapper around the trackJourneyEvent endpoint with the
 * product's attribution constants baked in. Callers only supply the stage and
 * the user's identity. Events must never block or break the UI: every call is
 * non-blocking and swallows errors (a console.debug at most). No retries.
 *
 * Server-to-server stages (provisioning, snapshots, network policy, audit
 * export, etc.) are fired from the Go backend (pkg/analytics). This module only
 * covers genuinely client-side moments: the launch-wizard template pick, the
 * >60s "connected" aha, capacity-dashboard views, and the post-login identify.
 */

// Publishable attribution constants — safe to ship in client code.
const WORKSPACE_ID = "48fb1ecf-6a66-4806-961f-f9934c50677a";
const PRODUCT_ID = "719f7f43-b262-4a30-8c22-54f0e65bffca";
const CAPTURE_KEY = "jk_live_EJX_GdDF9Zb0ASCVg6WTX03uHLIynS4O";

const ENDPOINT =
  "https://us-central1-marketingaimaster-6bed5.cloudfunctions.net/trackJourneyEvent";

// eventName used for the identify call that links a user to their CRM lead
// before they reach any stage.
export const IDENTIFY_EVENT_NAME = "session_start";

/**
 * Post an event body without blocking the caller. Uses fetch with keepalive so
 * the request survives a navigation/unmount. All errors are swallowed.
 */
function post(body) {
  try {
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch((err) => {
      // fire-and-forget: never surface to the user
      // eslint-disable-next-line no-console
      console.debug("[journeyAnalytics] send failed", err);
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.debug("[journeyAnalytics] send threw", err);
  }
}

/**
 * Normalize a user-like object into { userId, email }. Accepts the redux
 * `state.auth.user` shape (username/email) or an explicit object.
 */
function resolveIdentity(user) {
  if (!user) return { userId: "", email: "" };
  const userId = user.userId || user.username || user.id || "";
  const email = user.email || "";
  return { userId: String(userId), email };
}

/**
 * Fire a single journey-stage event. `stageId` must be one of the journey
 * slugs; eventName is set to the same slug.
 *
 * @param {string} stageId
 * @param {{username?: string, id?: string, userId?: string, email?: string}} user
 */
export function trackJourney(stageId, user) {
  const { userId, email } = resolveIdentity(user);
  if (!userId || !stageId) return;
  post({
    workspaceId: WORKSPACE_ID,
    productId: PRODUCT_ID,
    captureKey: CAPTURE_KEY,
    userId,
    email: email || undefined,
    stageId,
    eventName: stageId,
  });
}

/**
 * Send an identify-style event (eventName "session_start", no stageId) so the
 * user is linked to their CRM lead. Call right after login/signup.
 */
export function identifyJourney(user) {
  const { userId, email } = resolveIdentity(user);
  if (!userId) return;
  post({
    workspaceId: WORKSPACE_ID,
    productId: PRODUCT_ID,
    captureKey: CAPTURE_KEY,
    userId,
    email: email || undefined,
    eventName: IDENTIFY_EVENT_NAME,
  });
}
